import type { UserRole } from '@shopgnd/shared';
import type { HubNavIconId, WorkspaceNavGroup, WorkspaceNavItem } from '@/lib/workspaceNavigation';

/** Top-level admin sidebar sections (flat, always visible). */
export type AdminSectionId =
  | 'home'
  | 'inbox'
  | 'catalog'
  | 'money'
  | 'people'
  | 'site'
  | 'settings';

export type AdminSectionTab = {
  id: string;
  label: string;
  to: string;
  /** Nested tabs (e.g. SEO under Site). */
  children?: AdminSectionTab[];
};

export type AdminSectionConfig = {
  id: AdminSectionId;
  label: string;
  icon: HubNavIconId;
  to: string;
  tabs?: AdminSectionTab[];
  itemRoles?: UserRole[];
};

export const ADMIN_SECTIONS: AdminSectionConfig[] = [
  {
    id: 'home',
    label: 'Home',
    icon: 'layout-grid',
    to: '/hub/admin',
    tabs: [
      { id: 'dashboard', label: 'Dashboard', to: '/hub/admin' },
      { id: 'metrics', label: 'Metrics', to: '/hub/admin/metrics' },
    ],
  },
  {
    id: 'inbox',
    label: 'Inbox',
    icon: 'inbox',
    to: '/hub/admin/inbox',
    tabs: [
      { id: 'queue', label: 'Queue', to: '/hub/admin/inbox' },
      { id: 'approvals', label: 'Approvals', to: '/hub/admin/inbox/approvals' },
      { id: 'moderation', label: 'Moderation', to: '/hub/admin/inbox/moderation' },
      { id: 'support', label: 'Support', to: '/hub/admin/inbox/support' },
      { id: 'requests', label: 'Requests', to: '/hub/admin/inbox/requests' },
      { id: 'reviews', label: 'Reviews', to: '/hub/admin/inbox/reviews' },
    ],
  },
  {
    id: 'catalog',
    label: 'Catalog',
    icon: 'package',
    to: '/hub/admin/catalog/products',
    tabs: [
      { id: 'products', label: 'Products', to: '/hub/admin/catalog/products' },
      { id: 'properties', label: 'Properties', to: '/hub/admin/catalog/properties' },
      { id: 'classifieds', label: 'Classifieds', to: '/hub/admin/catalog/classifieds' },
      { id: 'categories', label: 'Categories', to: '/hub/admin/catalog/categories' },
    ],
  },
  {
    id: 'money',
    label: 'Orders & money',
    icon: 'wallet',
    to: '/hub/admin/money/orders',
    tabs: [
      { id: 'orders', label: 'Orders', to: '/hub/admin/money/orders' },
      { id: 'payouts', label: 'Payouts', to: '/hub/admin/money/payouts' },
      { id: 'subscriptions', label: 'Subscriptions', to: '/hub/admin/money/subscriptions' },
      { id: 'gift-cards', label: 'Gift cards', to: '/hub/admin/money/gift-cards' },
      { id: 'referrals', label: 'Referrals', to: '/hub/admin/money/referrals' },
      { id: 'fx', label: 'FX', to: '/hub/admin/money/fx' },
    ],
  },
  {
    id: 'people',
    label: 'People',
    icon: 'users',
    to: '/hub/admin/people/vendors',
    tabs: [
      { id: 'vendors', label: 'Vendors', to: '/hub/admin/people/vendors' },
      { id: 'pros', label: 'Service pros', to: '/hub/admin/people/pros' },
      { id: 'customers', label: 'Customers', to: '/hub/admin/people/customers' },
    ],
  },
  {
    id: 'site',
    label: 'Site & growth',
    icon: 'megaphone',
    to: '/hub/admin/site/ads',
    tabs: [
      { id: 'ads', label: 'Ads', to: '/hub/admin/site/ads' },
      { id: 'seo-workbench', label: 'SEO workbench', to: '/hub/admin/site/seo/workbench' },
      { id: 'seo-solar', label: 'Solar companies', to: '/hub/admin/site/seo/solar' },
      { id: 'seo-intros', label: 'Location intros', to: '/hub/admin/site/seo/intros' },
      { id: 'seo-redirects', label: 'Redirects', to: '/hub/admin/site/seo/redirects' },
      { id: 'documents', label: 'Legal docs', to: '/hub/admin/site/documents' },
      { id: 'audit', label: 'Audit log', to: '/hub/admin/site/audit' },
    ],
  },
  {
    id: 'settings',
    label: 'Settings',
    icon: 'settings',
    to: '/hub/admin/settings',
  },
];

/** Legacy admin paths → canonical section URLs (static segments only). */
export const ADMIN_LEGACY_PATH_REDIRECTS: Record<string, string> = {
  '/hub/admin/audit': '/hub/admin/site/audit',
  '/hub/admin/orders': '/hub/admin/money/orders',
  '/hub/admin/finance': '/hub/admin/money/payouts',
  '/hub/admin/subscriptions': '/hub/admin/money/subscriptions',
  '/hub/admin/products': '/hub/admin/catalog/products',
  '/hub/admin/properties': '/hub/admin/catalog/properties',
  '/hub/admin/classifieds': '/hub/admin/catalog/classifieds',
  '/hub/admin/categories': '/hub/admin/catalog/categories',
  '/hub/admin/vendors': '/hub/admin/people/vendors',
  '/hub/admin/pros': '/hub/admin/people/pros',
  '/hub/admin/approvals': '/hub/admin/inbox/approvals',
  '/hub/admin/requests': '/hub/admin/inbox/requests',
  '/hub/admin/reviews': '/hub/admin/inbox/reviews',
  '/hub/admin/moderation': '/hub/admin/inbox/moderation',
  '/hub/admin/support': '/hub/admin/inbox/support',
  '/hub/admin/communications': '/hub/admin/inbox/communications',
  '/hub/admin/ads': '/hub/admin/site/ads',
  '/hub/admin/documents': '/hub/admin/site/documents',
  '/hub/admin/seo/solar': '/hub/admin/site/seo/solar',
  '/hub/admin/seo/parish-intros': '/hub/admin/site/seo/intros',
  '/hub/admin/seo/redirects': '/hub/admin/site/seo/redirects',
  '/hub/admin/platform-settings': '/hub/admin/settings',
  '/hub/admin/fx': '/hub/admin/money/fx',
  '/hub/admin/gift-cards': '/hub/admin/money/gift-cards',
  '/hub/admin/gift-certificates': '/hub/admin/money/gift-cards',
  '/hub/admin/referrals': '/hub/admin/money/referrals',
};

export function adminSectionForPath(pathname: string): AdminSectionId | null {
  if (pathname === '/hub/admin' || pathname === '/hub/admin/metrics') return 'home';
  if (pathname.startsWith('/hub/admin/inbox')) return 'inbox';
  if (pathname.startsWith('/hub/admin/catalog')) return 'catalog';
  if (pathname.startsWith('/hub/admin/money')) return 'money';
  if (pathname.startsWith('/hub/admin/people')) return 'people';
  if (pathname.startsWith('/hub/admin/site')) return 'site';
  if (pathname.startsWith('/hub/admin/settings')) return 'settings';
  if (pathname === '/hub/admin/fulfillment') return null;
  return null;
}

export function adminNavItemIsActive(pathname: string, sectionId: AdminSectionId): boolean {
  const match = adminSectionForPath(pathname);
  if (match) return match === sectionId;
  const legacy = ADMIN_LEGACY_PATH_REDIRECTS[pathname];
  if (legacy) {
    const target = legacy.split('?')[0];
    return adminSectionForPath(target) === sectionId;
  }
  return false;
}

function filterSectionItems(sections: AdminSectionConfig[], userRoles: UserRole[]): AdminSectionConfig[] {
  return sections.filter((section) => {
    if (!section.itemRoles?.length) return true;
    return section.itemRoles.some((role) => userRoles.includes(role));
  });
}

/** Flat admin sidebar (single group, no accordion). */
export function buildAdminNav(userRoles: UserRole[] = []): WorkspaceNavGroup[] {
  const hasAdmin = userRoles.includes('admin') || userRoles.includes('platform_admin');
  if (!hasAdmin) return [];

  const items: WorkspaceNavItem[] = filterSectionItems(ADMIN_SECTIONS, userRoles).map((section) => ({
    to: section.to,
    label: section.label,
    icon: section.icon,
    end: section.id === 'home',
  }));

  return [{ title: 'Administration', items }];
}

export function getAdminSection(sectionId: AdminSectionId): AdminSectionConfig | undefined {
  return ADMIN_SECTIONS.find((s) => s.id === sectionId);
}

/** Flatten section tabs (including SEO children) for command palette search. */
export function flatAdminDestinations(): { label: string; to: string; section: string }[] {
  const rows: { label: string; to: string; section: string }[] = [];
  for (const section of ADMIN_SECTIONS) {
    if (!section.tabs?.length) {
      rows.push({ label: section.label, to: section.to, section: section.label });
      continue;
    }
    for (const tab of section.tabs) {
      if (tab.children?.length) {
        for (const child of tab.children) {
          rows.push({
            label: `${section.label} · ${tab.label} · ${child.label}`,
            to: child.to,
            section: section.label,
          });
        }
      } else {
        rows.push({ label: `${section.label} · ${tab.label}`, to: tab.to, section: section.label });
      }
    }
  }
  rows.push({ label: 'Fulfillment (legacy)', to: '/hub/admin/fulfillment', section: 'Legacy' });
  return rows;
}
